import { REDEEM_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { REDEEM_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { useGA, useWeb3 } from '@/hooks';
import { useClaimRedeemV1Mutation } from '@/store/services/redeem.service';
import { useFetchReferralStatsQuery } from '@/store/services/referral.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import { RedeemHook } from '../types/redeem.type';
import { ClaimRedeemProps } from '../types/redeem.type';

const useRedeemV1 = (): RedeemHook => {
  const { config, address } = useWeb3();
  const { trackButtonClickEvent } = useGA();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const [claimReemV1] = useClaimRedeemV1Mutation();
  const {
    isLoading: isLoadingReferralStats,
    data: referralStats,
    refetch: refetchReferralStats,
  } = useFetchReferralStatsQuery({ address: address as string, nft, page: 'redeem' }, { skip: !address || !nft });
  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);

  const handleClaimRedeem = useCallback(
    async ({ availableNode, nodeId, onSuccess }: ClaimRedeemProps) => {
      if (!address) return;
      setIsClaimingTrue();
      trackButtonClickEvent({ name: 'mint', address });
      try {
        const nftAddr = nodeId ?? nft;
        const claimRes = await claimReemV1({ nft: nftAddr, address });
        if (!claimRes.data || Object.keys(claimRes.data.signature).length === 0) {
          toast.error('Claim redeem failed. Please try again or contact to support');
          return;
        }
        const {
          nonce,
          signature: { v, s, r },
        } = claimRes.data;
        const hash = await writeContract(config, {
          abi: REDEEM_ABI,
          address: REDEEM_V1_SC_ADDRESS,
          functionName: 'redeem',
          args: [address, nftAddr, availableNode, nonce, v, r, s],
        });
        toast(TransactionSubmittedToast());
        await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed redeem successfully');
        refetchReferralStats();
        onSuccess?.();
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleRedeem');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim redeem failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [address, config, nft, claimReemV1, setIsClaimingFalse, setIsClaimingTrue, refetchReferralStats],
  );

  return {
    referralStats,
    isClaiming,
    isLoadingReferralStats,
    handleClaimRedeem,
  };
};

export { useRedeemV1 };
